
import ConfirmBox from "components/shared/VmwareMappingConfirmBox";
import { useState, useEffect } from "react";
import Breadcrumb from "components/shared/Breadcrumb";
import hisService from "services/admin/masters/hisService";
import Select from "react-select";
import MaterialTable from "material-table";
import { toast } from "react-toastify";
import {getMatrixId} from "utils/matriceHelpers";
import { MAPPING_INPUT_ERROR_TEMPLATE, MAPPING_SELECT_ERROR_TEMPLATE } from "constants/admin/masters/his";
import { ERROR_MESSAGE_UNKNOWN, 
  MSG_RECORD_CREATE, MSG_RECORD_DELETE, MSG_RECORD_UNDO_DELETE, MSG_RECORD_UPDATE } from "constants/common";

function ManageHISGpu() {

  const [breamcrumbs, setBreamcrumbs] = useState([
    { title: "UCP HIS Masters Mapping", url: "/admin/master/his/" },
    { title: "Manage Mapping" },
  ]);

  const serviceObj = new hisService();
  const [combineKeyList, setCombineKeyList] = useState([]);
  const [mappingList, setMappingList] = useState([{}]);
  const [gpuList, setGpuList] = useState([]);
  const [selectedCombineKeys, setSelectedCombineKeys] = useState([]);
  const [selectedGpu, setSelectedGpu] = useState(null);
  const [enteredGpuSoftware, setEnteredGpuSoftware] = useState("");
  const [combineKeyError, setCombineKeyError] = useState("");
  const [gpuError, setGpuError] = useState("");
  const [softwareError, setSoftwareError] = useState("");
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [itemToBeEdited, setItemToBeEdited] = useState(null);
  const [isDeleteConfirmationShown, setIsDeleteConfirmationShown] = useState(false);
  const [isRestoreConfirmationShown, setIsRestoreConfirmationShown] = useState(false);
  const [deleteConfirmationMessage, setDeleteConfirmationMessage] = useState("");
  const [restoreConfirmationMessage, setRestoreConfirmationMessage] = useState("");
  const [itemToBeDeletedOrRestored, setItemToBeDeletedOrRestored] = useState(null);

  const  Id  = getMatrixId(window.location.pathname);

  useEffect(() => getMappingData(), []);

  useEffect(() => {
    // re-validate form upon field value change if form is touched
    if (isFormTouched) {
      validateMappingForm();
    }
  }, [selectedCombineKeys, selectedGpu, enteredGpuSoftware]);

  useEffect(() => {
    // populate details of item to be edited
    if (isEditMode) {
      populateItemDetailsToEdit();
    }
  }, [itemToBeEdited?.record_details?.id]);

  const getMappingData = () => {
    serviceObj.getAllInitialData(Id)
      .then((response) => {
        setMappingList(response?.existing_mapping_list);
        extractAndSetCombineKeys(response?.his_combine_keys);
        extractAndSetGpuList(response?.master_drop_list);
      })
      .catch((error) => {
        toast.error(ERROR_MESSAGE_UNKNOWN);
      });
  };

  // setting Combine keys
  const extractAndSetCombineKeys = (dataList) => {
    const combineKeyList = dataList?.map((item) => ({
      id: item?.id,
      value: item?.combine_key,
      label: item?.combine_key,
      disabled: item?.disabled
    }));
    setCombineKeyList(combineKeyList);
  };

  const extractAndSetGpuList = (dataList) => {
    const gpuList = dataList?.map((item) => ({
      id: item?.id,
      value: item?.name,
      label: item?.name,
      disabled: item?.disabled
    }));
    setGpuList(gpuList);
  };

  // reset form values, error messages, form state, edit mode, target edit item
  const resetForm = () => {
    resetFormValues();
    resetErrorMessages();
    setIsFormTouched(false);
    setIsEditMode(false);
    setItemToBeEdited(null);
  };

  // append offending field name in error message
  const getSelectErrorMessageFor = (fieldName) => {
    return MAPPING_SELECT_ERROR_TEMPLATE?.replace("_FIELD_NAME_", fieldName);
  };

  // validate fields and set or reset error message
  const validateMappingForm = () => {
    let isFormValid = true;
    if (!selectedCombineKeys?.length) {
      setCombineKeyError(getSelectErrorMessageFor("Combine Key"));
      isFormValid = false;
    } else {
      setCombineKeyError("");
    }

    if (!selectedGpu?.value) {
      setGpuError(getSelectErrorMessageFor("GPU"));
      isFormValid = false;
    } else {
      setGpuError("");
    }
    return isFormValid;
  };

  // reset field values
  const resetFormValues = () => {
    setSelectedCombineKeys([]);
    setSelectedGpu([]);
    setEnteredGpuSoftware("");
  };

  // reset error messages
  const resetErrorMessages = () => {
    setCombineKeyError("");
    setGpuError("");
    setSoftwareError("");
  };

  // api call for adding new mapping
  const addMapping = (e) => {
    e.preventDefault();
    setIsFormTouched(true);
    const isFormValid = validateMappingForm();

    // call POST api if form is valid
    if (isFormValid) {
      let payload = {
        gpu_id: selectedGpu?.id,
        gpu_software: enteredGpuSoftware,
        his_combine_keys: selectedCombineKeys?.map(key => key?.id)
      };
      serviceObj.createGpuMapping(payload).then((response) => {
        toast.success(MSG_RECORD_CREATE);
        getMappingData();
        resetForm();
      });
    }
  };

  // process target item for editing
  const populateItemDetailsToEdit = () => {
    const combineKeysToEdit = itemToBeEdited?.his_combine_keys?.map((item) => ({
      id: item?.id,
      value: item?.combine_key,
      label: item?.combine_key,
      disabled: item?.disabled
    }));
    const gpuToEdit = {
      id: itemToBeEdited?.record_details?.gpu_id,
      value: itemToBeEdited?.record_details?.gpu,
      label: itemToBeEdited?.record_details?.gpu
    };
    setSelectedCombineKeys(combineKeysToEdit);
    setSelectedGpu(gpuToEdit);
    setEnteredGpuSoftware(itemToBeEdited?.record_details?.gpu_software);
  };

  // fetch details of item to be edited
  const editMapping = (data) => {
    setIsEditMode(true);
    // fetch details of item to edit
    serviceObj.getGpuMappingDetails(data?.id).then((response) => {
      setItemToBeEdited(response);
    })
      .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN));
  };

  // update 
  const updateMappingById = (e) => {
    e.preventDefault();
    setIsFormTouched(true);
    const isFormValid = validateMappingForm();
    if (isFormValid) {
      const payload = {
        his_combine_keys: selectedCombineKeys?.map(key => key?.id),
        gpu_id: selectedGpu?.id,
        gpu_software: enteredGpuSoftware
      };
      const targetItemId = itemToBeEdited?.record_details?.id;
      serviceObj.updateGpuMappingById(targetItemId, payload).then((response) => {
        toast.success(MSG_RECORD_UPDATE);
        getMappingData();
        resetForm();
      })
        .catch(error => toast.error(ERROR_MESSAGE_UNKNOWN)
        );
    }
  };

  // delete or restore mapping
  const deleteOrRestoreMappingById = () => {
    const { id, is_deleted } = itemToBeDeletedOrRestored;
    serviceObj.deleteGpuMappingById(id)
      .then((response) => {
        // show deleted or restored message based on is_deleted property
        if (is_deleted) {
          toast.success(MSG_RECORD_UNDO_DELETE);
          setIsRestoreConfirmationShown(false);
          setRestoreConfirmationMessage("")
        } else {
          toast.success(MSG_RECORD_DELETE);
          setIsDeleteConfirmationShown(false)
          setDeleteConfirmationMessage("");
        }
        resetForm();
        setItemToBeDeletedOrRestored(null);
      })
      .catch((error) => {
        toast.error(ERROR_MESSAGE_UNKNOWN);
        setIsDeleteConfirmationShown(false)
        setDeleteConfirmationMessage("");
        setItemToBeDeletedOrRestored(null);
      })
      .finally(() => getMappingData());
  };

  // show delete warning confirmation popup
  const deleteMappingHandler = (rowData) => {
    setIsDeleteConfirmationShown(true);
    setItemToBeDeletedOrRestored(rowData);
    setDeleteConfirmationMessage(`Are you sure you want to remove ${rowData?.gpu} with Combine Key ${rowData?.his_combine_key}?`)
  };

  // show restore warning confirmation popup
  const restoreMappingHandler = (rowData) => {
    setIsRestoreConfirmationShown(true);
    setItemToBeDeletedOrRestored(rowData);
    setRestoreConfirmationMessage(`Are you sure you want to restore ${rowData?.gpu} with Combine Key ${rowData?.his_combine_key}?`);
  };

  // hide confirmation message and reset target item
  const cancelDeleteItem = () => {
    setIsDeleteConfirmationShown(false);
    setItemToBeDeletedOrRestored(null);
  };

  // hide confirmation message and reset target item
  const cancelRestoreItem = () => {
    setIsRestoreConfirmationShown(false);
    setItemToBeDeletedOrRestored(null);
    setRestoreConfirmationMessage("")
  };

  return (
    <div className="form-wrap mt-3">
      <Breadcrumb data={breamcrumbs} disableEditTitle={true} />
      {isDeleteConfirmationShown &&
        <ConfirmBox
          actionHandler={deleteOrRestoreMappingById}
          handleSubmit={deleteOrRestoreMappingById}
          cancelDeleteItem={cancelDeleteItem}
        >
          {deleteConfirmationMessage}
        </ConfirmBox>
      }
      {isRestoreConfirmationShown &&
        <ConfirmBox
          actionHandler={deleteOrRestoreMappingById}
          handleSubmit={deleteOrRestoreMappingById}
          cancelDeleteItem={cancelRestoreItem}
        >
          {restoreConfirmationMessage}
        </ConfirmBox>
      }
      <div className="form-group d-flex">
        <div className="col-sm-4">
          <form>
            <div className="row pl-2 pb-1">
              <div className="col-sm-11">
                {/* Combine key */}
                <div className="form-group">
                  <label htmlFor={""} className="mr-sm-2 mt-2"><b>HIS Combine Key</b></label>
                  {/* add multi select dropdown */}
                  <Select
                    isMulti
                    name="Combine Key"
                    options={combineKeyList}
                    classNamePrefix="select"
                    onChange={selectedKeys => setSelectedCombineKeys(selectedKeys)}
                    value={selectedCombineKeys}
                    isOptionDisabled={option => option.disabled}
                  />
                  {
                    combineKeyError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{combineKeyError}</div>
                  }
                </div>
                {/* GPU */}
                <div className="form-group">
                  <label htmlFor={"gpu"} className="mr-sm-2 mt-2"><b>GPU</b></label>
                  <Select
                    name="gpu"
                    options={gpuList}
                    classNamePrefix="select"
                    onChange={selectedId => setSelectedGpu(selectedId)}
                    value={selectedGpu}
                    isOptionDisabled={option => option.disabled}
                    isDisabled={isEditMode}
                  />
                  {
                    gpuError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{gpuError}</div>
                  }
                </div>
                
                
                {/* GPU Software */}
                <div className="form-group">
                  <label htmlFor={"gpuSoftware"} className="mr-sm-2"><b>GPU Software</b></label>
                  <input type="text"
                    id="gpuSoftware"
                    className="form-control"
                    maxLength="60"
                    name="gpuSoftware"
                    value={enteredGpuSoftware}
                    onChange={(e) => setEnteredGpuSoftware(e.target.value)}
                    placeholder="Enter GPU Software"
                  />
                  {softwareError
                    &&
                    <div className="col-sm-10 mb-2 text-danger">{softwareError}</div>
                  }
                </div>
              </div>
            </div>

            <div className="col-sm-12">
              <button type="reset"
                onClick={resetForm}
                className="btn btn-md btn-light btn-link submit-btn-inline mr-sm-2">
                <i className="fa fa-refresh"></i> Reset</button>
              <button
                type="submit"
                className="btn btn-md btn-dark submit-btn-inline"
                onClick={isEditMode ? updateMappingById : addMapping}
              >
                {isEditMode ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </div>
        <div className="col-sm-8">
          <MaterialTable
            title="GPU Mappings"
            columns={[
              { title: "Combine Key", field: "his_combine_key" },
              { title: "GPU", field: "gpu" },
              { title: "GPU Software", field: "gpu_software" }
            ]}
            data={mappingList}
            options={{
              headerStyle: {
                backgroundColor: "#e9ecef",
              },
              actionsColumnIndex: -1,
              selection: false,
              search: true,
              maxBodyHeight: 400
            }}

            localization={{
              header: {
                actions: "Actions",
              },
            }}
            actions={
              [
                (rowData) => ({
                  icon: "edit",
                  tooltip: !rowData.is_deleted && "Edit Record",
                  onClick: (e, data) => editMapping(data),
                  hidden: !rowData?.id || rowData.is_deleted // display action only if any valid item is present
                }),
                (rowData) => ({
                  icon: rowData.is_deleted ? "undo" : "delete",
                  tooltip: rowData.is_deleted ? "Undo Deleted Record" : "Delete Record",
                  onClick: (e, data) => rowData.is_deleted ? restoreMappingHandler(data) : deleteMappingHandler(data),
                  hidden: !rowData?.id // display action only if any valid item is present
                })
              ]}
          />
        </div>
      </div>
    </div>
  )
}

export default ManageHISGpu;